import { useRef, useState } from 'react'
import {
  Box,
  VStack,
  HStack,
  Stack,
  Text,
  Button,
  Spinner,
  Badge,
  Table,
  IconButton,
  useDisclosure,
  SimpleGrid,
  Checkbox,
  Dialog,
  Portal,
  Separator,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiEdit2, FiTrash2, FiArrowLeft, FiPhone, FiMail, FiGlobe, FiMapPin, FiUsers, FiPlus } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  useUnionDetail, useDeleteUnion, useUpdateBusinessAffiliation,
  useUnaffiliateBusinessFromUnion, useUnaffiliateWorksiteFromUnion,
  useUpdateWorksiteAffiliation, useUpdateWorksiteOverride,
  useCreateWorksiteOverride, useRemoveWorksiteOverride, useAddSteward, useRemoveSteward,
} from '../hooks/useUnion'
import { worksiteApi, businessApi } from '../lib/api'
import { UnionFormModal } from '../components/union/UnionFormModal'
import { AffiliateBusinessModal } from '../components/union/AffiliateBusinessModal'
import { InfoField } from '../components/shared/InfoField'
import { EditContractModal } from '../components/union/EditContractModal'
import type { ContractEditTarget } from '../components/union/EditContractModal'
import { UnionBusinessesSection } from '../components/union/UnionBusinessesSection'
import { UnionStandaloneWorksitesSection } from '../components/union/UnionStandaloneWorksitesSection'
import { UnionMemberTable } from '../components/union/UnionMemberTable'
import { AddMemberWizard } from '../components/business/AddMemberWizard'
import type { UnionBusinessAffiliation, UnionWorksiteAffiliation } from '../types/union'

export function UnionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const cancelRef = useRef<HTMLButtonElement>(null!)
  const editModal = useDisclosure()
  const affiliateModal = useDisclosure()
  const addMemberModal = useDisclosure()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [expandedBusinessIds, setExpandedBusinessIds] = useState<Set<string>>(new Set())
  const [expandedWorksiteIds, setExpandedWorksiteIds] = useState<Set<string>>(new Set())
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'business' | 'standalone'; id: string; name: string } | null>(null)
  const [addingStewardFor, setAddingStewardFor] = useState<string | null>(null)
  const [showInactive, setShowInactive] = useState(false)
  const [contractEditTarget, setContractEditTarget] = useState<ContractEditTarget | null>(null)

  const { data: union, isLoading, error } = useUnionDetail(id)
  const deleteMutation = useDeleteUnion()
  const updateBusinessAffiliation = useUpdateBusinessAffiliation(id || '')
  const unaffiliateBusiness = useUnaffiliateBusinessFromUnion(id || '')
  const unaffiliateWorksite = useUnaffiliateWorksiteFromUnion(id || '')
  const updateWorksiteAffiliation = useUpdateWorksiteAffiliation(id || '')
  const updateWorksiteOverride = useUpdateWorksiteOverride(id || '')
  const createWorksiteOverride = useCreateWorksiteOverride(id || '')
  const removeWorksiteOverride = useRemoveWorksiteOverride(id || '')
  const addSteward = useAddSteward(id || '')
  const removeSteward = useRemoveSteward(id || '')

  const toggleBusinessExpand = (businessUnionId: string) => {
    setExpandedBusinessIds(prev => {
      const next = new Set(prev)
      if (next.has(businessUnionId)) next.delete(businessUnionId)
      else next.add(businessUnionId)
      return next
    })
  }

  const toggleWorksiteExpand = (worksiteUnionId: string) => {
    setExpandedWorksiteIds(prev => {
      const next = new Set(prev)
      if (next.has(worksiteUnionId)) next.delete(worksiteUnionId)
      else next.add(worksiteUnionId)
      return next
    })
  }

  const handleDelete = async () => {
    if (!id) return
    try {
      await deleteMutation.mutateAsync(id)
      toaster.create({ title: 'Union deleted', type: 'success', duration: 3000 })
      navigate('/dashboard/unions')
    } catch {
      toaster.create({ title: 'Failed to delete union', type: 'error', duration: 3000 })
    }
  }

  const handleDeleteAffiliation = async () => {
    if (!deleteTarget) return
    try {
      if (deleteTarget.type === 'business') {
        await unaffiliateBusiness.mutateAsync(deleteTarget.id)
      } else {
        await unaffiliateWorksite.mutateAsync(deleteTarget.id)
      }
      toaster.create({ title: 'Affiliation removed', type: 'success', duration: 3000 })
      setDeleteTarget(null)
    } catch {
      toaster.create({ title: 'Failed to remove affiliation', type: 'error', duration: 3000 })
    }
  }

  const handleBusinessFieldSave = (ab: UnionBusinessAffiliation, fieldName: string, value: string | null, name?: string | null) => {
    updateBusinessAffiliation.mutate({
      businessId: ab.businessId,
      data: {
        contractStartDate: ab.contractStartDate,
        contractEndDate: ab.contractEndDate,
        unionRepName: fieldName === 'unionRepName' ? (value || undefined) : ab.unionRepName,
        employerContactId: fieldName === 'employerContactId' ? (value || undefined) : ab.employerContactId,
        employerContactName: fieldName === 'employerContactId' ? (name || undefined) : ab.employerContactName,
      },
    }, {
      onSuccess: () => toaster.create({ title: 'Updated', type: 'success', duration: 2000 }),
      onError: () => toaster.create({ title: 'Failed to update', type: 'error', duration: 3000 }),
    })
  }

  const handleBusinessStatusChange = (ab: UnionBusinessAffiliation, isActive: boolean) => {
    updateBusinessAffiliation.mutate({
      businessId: ab.businessId,
      data: {
        contractStartDate: ab.contractStartDate,
        contractEndDate: ab.contractEndDate,
        unionRepName: ab.unionRepName,
        employerContactId: ab.employerContactId,
        employerContactName: ab.employerContactName,
        isActive,
      },
    }, {
      onSuccess: () => toaster.create({ title: `Status changed to ${isActive ? 'Active' : 'Inactive'}`, type: 'success', duration: 2000 }),
      onError: () => toaster.create({ title: 'Failed to update status', type: 'error', duration: 3000 }),
    })
  }

  const handleBusinessNotesSave = async (businessId: string, _fieldName: string, value: string) => {
    try {
      await businessApi.updateNotes(businessId, { notes: value || undefined })
      toaster.create({ title: 'Notes updated', type: 'success', duration: 2000 })
      queryClient.invalidateQueries({ queryKey: ['unions', id] })
    } catch {
      toaster.create({ title: 'Failed to update notes', type: 'error', duration: 3000 })
    }
  }

  const handleWorksiteNotesSave = async (worksiteId: string, _fieldName: string, value: string) => {
    try {
      await worksiteApi.updateNotes(worksiteId, { notes: value || undefined })
      toaster.create({ title: 'Notes updated', type: 'success', duration: 2000 })
      queryClient.invalidateQueries({ queryKey: ['unions', id] })
    } catch {
      toaster.create({ title: 'Failed to update notes', type: 'error', duration: 3000 })
    }
  }

  const handleStandaloneStatusChange = (aw: UnionWorksiteAffiliation, isActive: boolean) => {
    updateWorksiteAffiliation.mutate({
      worksiteId: aw.worksiteId,
      data: {
        contractStartDate: aw.contractStartDate,
        contractEndDate: aw.contractEndDate,
        unionRepName: aw.unionRepName,
        employerContactId: aw.employerContactId,
        employerContactName: aw.employerContactName,
        isActive,
      },
    }, {
      onSuccess: () => toaster.create({ title: `Status changed to ${isActive ? 'Active' : 'Inactive'}`, type: 'success', duration: 2000 }),
      onError: () => toaster.create({ title: 'Failed to update status', type: 'error', duration: 3000 }),
    })
  }

  const handleStandaloneFieldSave = (aw: UnionWorksiteAffiliation, fieldName: string, value: string | null, name?: string | null) => {
    updateWorksiteAffiliation.mutate({
      worksiteId: aw.worksiteId,
      data: {
        contractStartDate: aw.contractStartDate,
        contractEndDate: aw.contractEndDate,
        unionRepName: fieldName === 'unionRepName' ? (value || undefined) : aw.unionRepName,
        employerContactId: fieldName === 'employerContactId' ? (value || undefined) : aw.employerContactId,
        employerContactName: fieldName === 'employerContactId' ? (name || undefined) : aw.employerContactName,
      },
    }, {
      onSuccess: () => toaster.create({ title: 'Updated', type: 'success', duration: 2000 }),
      onError: () => toaster.create({ title: 'Failed to update', type: 'error', duration: 3000 }),
    })
  }

  const handleWorksiteRepSave = (ab: UnionBusinessAffiliation, wo: UnionWorksiteAffiliation, repName: string | null) => {
    if (wo.isOverride && wo.worksiteUnionId) {
      updateWorksiteOverride.mutate({
        businessId: ab.businessId,
        worksiteId: wo.worksiteId,
        data: {
          unionRepName: repName || undefined,
          employerContactId: wo.employerContactId,
          employerContactName: wo.employerContactName,
        },
      }, {
        onSuccess: () => toaster.create({ title: 'Rep updated', type: 'success', duration: 2000 }),
        onError: () => toaster.create({ title: 'Failed to update', type: 'error', duration: 3000 }),
      })
    } else {
      createWorksiteOverride.mutate({
        businessId: ab.businessId,
        data: {
          worksiteId: wo.worksiteId,
          isExcluded: false,
          unionRepName: repName || undefined,
        },
      }, {
        onSuccess: () => toaster.create({ title: 'Rep override created', type: 'success', duration: 2000 }),
        onError: () => toaster.create({ title: 'Failed to create override', type: 'error', duration: 3000 }),
      })
    }
  }

  const handleWorksiteStatusChange = (ab: UnionBusinessAffiliation, wo: UnionWorksiteAffiliation, newStatus: 'covered' | 'local' | 'excluded') => {
    const currentStatus = wo.isExcluded ? 'excluded' : wo.isOverride ? 'local' : 'covered'
    if (currentStatus === newStatus) return

    if (newStatus === 'covered') {
      removeWorksiteOverride.mutate({
        businessId: ab.businessId,
        worksiteId: wo.worksiteId,
      }, {
        onSuccess: () => toaster.create({ title: 'Worksite set to Covered', type: 'success', duration: 2000 }),
        onError: () => toaster.create({ title: 'Failed to update status', type: 'error', duration: 3000 }),
      })
    } else if (currentStatus === 'covered') {
      createWorksiteOverride.mutate({
        businessId: ab.businessId,
        data: {
          worksiteId: wo.worksiteId,
          isExcluded: newStatus === 'excluded',
        },
      }, {
        onSuccess: () => toaster.create({ title: `Worksite set to ${newStatus === 'excluded' ? 'Excluded' : 'Local'}`, type: 'success', duration: 2000 }),
        onError: () => toaster.create({ title: 'Failed to update status', type: 'error', duration: 3000 }),
      })
    } else {
      updateWorksiteOverride.mutate({
        businessId: ab.businessId,
        worksiteId: wo.worksiteId,
        data: {
          isExcluded: newStatus === 'excluded',
          unionRepName: wo.unionRepName,
          employerContactId: wo.employerContactId,
          employerContactName: wo.employerContactName,
        },
      }, {
        onSuccess: () => toaster.create({ title: `Worksite set to ${newStatus === 'excluded' ? 'Excluded' : 'Local'}`, type: 'success', duration: 2000 }),
        onError: () => toaster.create({ title: 'Failed to update status', type: 'error', duration: 3000 }),
      })
    }
  }

  const handleContractSave = (startDate?: string, endDate?: string) => {
    if (!contractEditTarget) return
    const t = contractEditTarget
    const callbacks = {
      onSuccess: () => { toaster.create({ title: 'Contract updated', type: 'success', duration: 2000 }); setContractEditTarget(null) },
      onError: () => toaster.create({ title: 'Failed to update contract', type: 'error', duration: 3000 }),
    }

    if (t.type === 'business') {
      updateBusinessAffiliation.mutate({
        businessId: t.businessId!,
        data: { contractStartDate: startDate, contractEndDate: endDate },
      }, callbacks)
    } else if (t.type === 'worksite-override') {
      if (t.isOverride) {
        updateWorksiteOverride.mutate({
          businessId: t.businessId!,
          worksiteId: t.worksiteId!,
          data: {
            contractStartDate: startDate,
            contractEndDate: endDate,
            isExcluded: t.isExcluded,
            unionRepName: t.unionRepName,
            employerContactId: t.employerContactId,
            employerContactName: t.employerContactName,
          },
        }, callbacks)
      } else {
        createWorksiteOverride.mutate({
          businessId: t.businessId!,
          data: {
            worksiteId: t.worksiteId!,
            isExcluded: false,
            contractStartDate: startDate,
            contractEndDate: endDate,
          },
        }, callbacks)
      }
    } else {
      updateWorksiteAffiliation.mutate({
        worksiteId: t.worksiteId!,
        data: { contractStartDate: startDate, contractEndDate: endDate },
      }, callbacks)
    }
  }

  const handleAddSteward = (worksiteId: string, contactId: string | null, name?: string | null, role?: string) => {
    addSteward.mutate({
      worksiteId,
      data: { contactId: contactId || undefined, name: name || undefined, role },
    }, {
      onSuccess: () => {
        toaster.create({ title: 'Steward added', type: 'success', duration: 2000 })
        setAddingStewardFor(null)
      },
      onError: () => toaster.create({ title: 'Failed to add steward', type: 'error', duration: 3000 }),
    })
  }

  const handleRemoveSteward = (worksiteId: string, stewardId: string) => {
    removeSteward.mutate({ worksiteId, stewardId }, {
      onSuccess: () => toaster.create({ title: 'Steward removed', type: 'success', duration: 2000 }),
      onError: () => toaster.create({ title: 'Failed to remove steward', type: 'error', duration: 3000 }),
    })
  }

  if (isLoading) {
    return (
      <VStack py={12}>
        <Spinner size="lg" color="accent" />
        <Text color="gray.500">Loading union...</Text>
      </VStack>
    )
  }

  if (!!error || !union) {
    return (
      <Box p={{ base: 4, md: 8 }}>
        <Box bg="red.50" p={4} borderRadius="md" color="red.600">
          Union not found or an error occurred.
        </Box>
        <Button mt={4} variant="ghost" onClick={() => navigate('/dashboard/unions')}><FiArrowLeft />Back to Unions
                  </Button>
      </Box>
    );
  }

  const filteredBusinesses = showInactive
    ? union.affiliatedBusinesses
    : union.affiliatedBusinesses.filter(ab => ab.isActive)
  const filteredWorksites = showInactive
    ? union.affiliatedWorksites
    : union.affiliatedWorksites.filter(aw => aw.isActive)
  const totalAffiliations = union.affiliatedBusinesses.length + union.affiliatedWorksites.length

  return (
    <Box p={{ base: 4, md: 8 }}>
      <VStack gap={6} align="stretch">
        {/* Back + actions header */}
        <HStack justify="space-between" flexWrap="wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/unions')}><FiArrowLeft />Back to Unions
                      </Button>
          <Stack direction={{ base: 'column', md: 'row' }} gap={2}>
            <Button size="sm" colorPalette="brand" onClick={editModal.onOpen}><FiEdit2 />Edit
                          </Button>
            <IconButton
              aria-label="Delete"
              size="sm"
              variant="ghost"
              colorPalette="red"
              onClick={() => setShowDeleteDialog(true)}><FiTrash2 /></IconButton>
          </Stack>
        </HStack>

        {/* Union card */}
        <Box bg="surface" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
          <HStack gap={4} mb={4}>
            <Box
              w={12} h={12} bg="purple.50" borderRadius="lg"
              display="flex" alignItems="center" justifyContent="center"
            >
              <Box boxSize={6} color="purple.500" asChild><FiUsers /></Box>
            </Box>
            <Box flex={1}>
              <HStack gap={2}>
                <Text fontSize="xl" fontWeight="bold">{union.fullName || union.name}</Text>
                {!union.isActive && <Badge colorPalette="red">Inactive</Badge>}
              </HStack>
              {union.localNumber && (
                <Text fontSize="sm" color="gray.500">Local #{union.localNumber}</Text>
              )}
              {union.description && (
                <Text fontSize="sm" color="gray.600" mt={1}>{union.description}</Text>
              )}
            </Box>
          </HStack>

          <Separator mb={4} />

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
            {union.region && (
              <HStack align="flex-start" gap={2}>
                <Box boxSize={4} color="gray.400" mt={0.5} asChild><FiMapPin /></Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">Region</Text>
                  <Text fontSize="sm">{union.region}</Text>
                </Box>
              </HStack>
            )}
            {union.phone && (
              <HStack align="flex-start" gap={2}>
                <Box boxSize={4} color="gray.400" mt={0.5} asChild><FiPhone /></Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">Phone</Text>
                  <Text fontSize="sm">{union.phone}</Text>
                </Box>
              </HStack>
            )}
            {union.email && (
              <HStack align="flex-start" gap={2}>
                <Box boxSize={4} color="gray.400" mt={0.5} asChild><FiMail /></Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">Email</Text>
                  <Text fontSize="sm">{union.email}</Text>
                </Box>
              </HStack>
            )}
            {union.website && (
              <HStack align="flex-start" gap={2}>
                <Box boxSize={4} color="gray.400" mt={0.5} asChild><FiGlobe /></Box>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">Website</Text>
                  <Text fontSize="sm">{union.website}</Text>
                </Box>
              </HStack>
            )}
            <InfoField label="Name" value={union.name !== union.fullName ? union.name : undefined} />
          </SimpleGrid>
        </Box>

        {/* Affiliations */}
        <Box bg="surface" p={{ base: 4, md: 6 }} borderRadius="lg" shadow="md">
          <HStack justify="space-between" mb={4}>
            <HStack gap={4}>
              <Text fontSize="lg" fontWeight="semibold">
                Affiliations
              </Text>
              <Checkbox.Root
                size="sm"
                onCheckedChange={(e) => setShowInactive(e.checked === true)}
                checked={showInactive}
              ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                <Text fontSize="xs" color="gray.500">Show Inactive</Text>
              </Checkbox.Label></Checkbox.Root>
            </HStack>
            <Button
              size="xs"
              colorPalette="brand"
              variant="outline"
              onClick={affiliateModal.onOpen}><FiPlus />Add
                          </Button>
          </HStack>
          {totalAffiliations === 0 ? (
            <Text color="gray.500" fontSize="sm">No affiliations.</Text>
          ) : (
            <Box overflowX="auto">
              <Table.Root size="sm">
                <Table.Header bg="gray.50">
                  <Table.Row>
                    <Table.ColumnHeader>Scope</Table.ColumnHeader>
                    <Table.ColumnHeader>Name</Table.ColumnHeader>
                    <Table.ColumnHeader>Type</Table.ColumnHeader>
                    <Table.ColumnHeader>Contract Period</Table.ColumnHeader>
                    <Table.ColumnHeader>Status</Table.ColumnHeader>
                    <Table.ColumnHeader w="40px"></Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {/* Business-level affiliations */}
                  <UnionBusinessesSection
                    filteredBusinesses={filteredBusinesses}
                    expandedBusinessIds={expandedBusinessIds}
                    toggleBusinessExpand={toggleBusinessExpand}
                    handleBusinessStatusChange={handleBusinessStatusChange}
                    handleBusinessFieldSave={handleBusinessFieldSave}
                    handleBusinessNotesSave={handleBusinessNotesSave}
                    handleWorksiteStatusChange={handleWorksiteStatusChange}
                    handleWorksiteRepSave={handleWorksiteRepSave}
                    handleWorksiteNotesSave={handleWorksiteNotesSave}
                    handleRemoveSteward={handleRemoveSteward}
                    setContractEditTarget={setContractEditTarget}
                    setDeleteTarget={setDeleteTarget}
                    addingStewardFor={addingStewardFor}
                    setAddingStewardFor={setAddingStewardFor}
                    handleAddSteward={handleAddSteward}
                  />

                  {/* Standalone worksite affiliations */}
                  <UnionStandaloneWorksitesSection
                    filteredWorksites={filteredWorksites}
                    expandedWorksiteIds={expandedWorksiteIds}
                    toggleWorksiteExpand={toggleWorksiteExpand}
                    handleStandaloneStatusChange={handleStandaloneStatusChange}
                    handleStandaloneFieldSave={handleStandaloneFieldSave}
                    handleWorksiteNotesSave={handleWorksiteNotesSave}
                    handleRemoveSteward={handleRemoveSteward}
                    setContractEditTarget={setContractEditTarget}
                    setDeleteTarget={setDeleteTarget}
                    addingStewardFor={addingStewardFor}
                    setAddingStewardFor={setAddingStewardFor}
                    handleAddSteward={handleAddSteward}
                  />
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Box>

        {/* Members — paginated + searchable (DT-SCALE-1): never loads the whole membership at once */}
        {id && (
          <Box>
            <HStack justify="flex-end" mb={2}>
              <Button
                size="sm"
                colorPalette="brand"
                onClick={addMemberModal.onOpen}
              ><FiPlus />Add Member
              </Button>
            </HStack>
            <UnionMemberTable unionId={id} memberCount={union.memberCount} />
          </Box>
        )}
      </VStack>

      {/* Edit modal */}
      {editModal.open && (
        <UnionFormModal isOpen={editModal.open} onClose={editModal.onClose} union={union} />
      )}

      {/* Affiliate modal */}
      {affiliateModal.open && id && (
        <AffiliateBusinessModal
          isOpen={affiliateModal.open}
          onClose={affiliateModal.onClose}
          unionId={id}
        />
      )}

      {/* Add member wizard, pre-selecting this page's union on step 3 */}
      {addMemberModal.open && id && (
        <AddMemberWizard
          isOpen={addMemberModal.open}
          onClose={addMemberModal.onClose}
          unionId={id}
        />
      )}

      {/* Edit contract period modal */}
      <EditContractModal
        target={contractEditTarget}
        onClose={() => setContractEditTarget(null)}
        onSave={handleContractSave}
        isLoading={updateBusinessAffiliation.isPending || updateWorksiteOverride.isPending || createWorksiteOverride.isPending || updateWorksiteAffiliation.isPending}
      />

      {/* Delete union confirmation */}
      <Dialog.Root
        open={showDeleteDialog}
        initialFocusEl={() => cancelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            setShowDeleteDialog(false);
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">Delete Union</Dialog.Header>
                <Dialog.Body>
                  Are you sure you want to delete <strong>{union.fullName || union.name}</strong>? This will deactivate the union record.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDelete}
                    ml={3}
                    loading={deleteMutation.isPending}
                  >
                    Delete
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      {/* Delete affiliation confirmation */}
      <Dialog.Root
        open={!!deleteTarget}
        initialFocusEl={() => cancelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            setDeleteTarget(null);
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">Remove Affiliation</Dialog.Header>
                <Dialog.Body>
                  {deleteTarget?.type === 'business' ? (
                    <>
                      Removing this affiliation will delete all information about <strong>{deleteTarget.name}</strong>'s
                      relationship with this union, including worksite-level overrides and steward assignments.
                      Notes on the business and its worksites will be preserved.
                    </>
                  ) : (
                    <>
                      Removing this affiliation will delete the union relationship with <strong>{deleteTarget?.name}</strong>,
                      including any steward assignments. Notes on the worksite will be preserved.
                    </>
                  )}
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={() => setDeleteTarget(null)}>Cancel</Button>
                  <Button
                    colorPalette="red"
                    onClick={handleDeleteAffiliation}
                    ml={3}
                    loading={unaffiliateBusiness.isPending || unaffiliateWorksite.isPending}
                  >
                    Remove
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>
    </Box>
  );
}
